import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as clientErrTypes from 'config/clientErrorTypes';
import { lotteryInstantTypes, lotteryParticipantTypes } from 'config/promoLotteryTypes';
import { gameTypes } from 'config/promoGameTypes';
import {
    isGameShouldOpenWinnerAfterGet,
    isLotteryShouldOpenWinnerAfterGet,
    isNoResultPageGames
} from 'config/promoGameTypesClientConfig';
import { getPrizeTypeLabel } from 'config/promoPrizeTypes';
import {
    getWinner,
    checkIsQualifyToGetWinner,
    updateWinnerAnimationFlag,
    getPromoLotteries,
    getPrizeInfoAPI
} from 'api/client';
import { mgmLotteryValidationApi, createMgmEntryApi } from 'api/mgm/client';
import {
    setParticipantWinnerPopup,
    setShouldUpdateUserWinnerList,
    PICK_PRIZE_CUSTOM_PRIZE_UUID
} from 'client/redux/modules/flags';
import { successGetWinner } from 'client/redux/modules/userEntryState';
import {
    updateParticipantRemainderFromApi,
    updateParticipantRemainder
} from 'client/redux/modules/lotteryParticipant';
import { setCurrentPrizeUUID, setGameAlreadyToPlay } from 'client/redux/modules/purePrizeState';
import { updateFlowOfEntryRecord, entryFlowKeys } from 'client/redux/modules/userGameResultRecords';
import { updateGameResultRecordsCount } from 'client/game_checkIn/redux/modules/gameState';
import { scrollPromoBodyIntoView } from 'client/utils/scroll';
import { gtmPushPrize } from 'client/utils/tracking';
import useClientErrorHandler, { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import useClientHistory, { JOIN, RESULT, LOTTERY, PRIZE } from 'client/hooks/useClientHistory';
import { useIsVerifyModuleCanGetParticipantWinner } from 'client/hooks/moduleVerifyUtilHooks';
import usePrizeRuleChecker from 'client/hooks/usePrizeRuleChecker';
import {
    getLotteryConfig,
    lotteryCategories
} from 'editor/components/SideBar/Prize/configs/prizeTypeConfig';
import { delay } from 'utils/delay';

export default function useGetParticipantWinner() {
    const dispatch = useDispatch();
    const history = useClientHistory();
    const location = useLocation();
    const errHandler = useClientErrorHandler();
    const getVerifyModuleStatus = useIsVerifyModuleCanGetParticipantWinner();
    const { uuid: promoUUID, type: gameType, id: promoId } = useSelector(s => s.promo);
    const { isPurePrizeGame } = useSelector(s => s.promoState);
    const { isMgmShare, isMgmMission, isMgmSelf } = useSelector(s => s.flags);
    const lotteryParticipant = useSelector(s => s.lotteryParticipant);
    const lotteryInstant = useSelector(s => s.lotteryInstant);
    const currentEntryUUID = useSelector(s => s.userEntryState.currentEntryUUID);
    const {
        isLotteryInstantRequiredAllCorrect,
        isLotteryParticipantRequiredAllCorrect,
        checkIsGameChallengeAllCorrect,
        checkIsUserHaveClaimedParticipantPrize,
        checkIsUserHaveClaimedInstantPrize
    } = usePrizeRuleChecker({ entryUuid: currentEntryUUID });

    const {
        lotteryParticipant: mgmLotteryParticipant = {},
        lotteryInstant: mgmLotteryInstant = {}
    } = useSelector(s => s.mgmReferral);

    const { loginId } = useSelector(s => s.userLoginState);

    const currentLotteryParticipant = isMgmShare ? mgmLotteryParticipant : lotteryParticipant;
    const currentLotteryInstant = isMgmShare ? mgmLotteryInstant : lotteryInstant;
    //動畫獎
    const animatingLottery = {
        uuid: currentLotteryParticipant.uuid,
        type: currentLotteryParticipant.type,
        AnimationSettings: currentLotteryParticipant.AnimationSettings
    };
    //即時獎
    const instantLottery = {
        uuid: currentLotteryInstant.uuid,
        type: currentLotteryInstant.type,
        AnimationSettings: {}
    };

    const {
        currentEntryId,
        currentEntryUUID: entriesUUID,
        hasInstantPrize: hasFulfillLotteryInstant,
        hasParticipantPrize: hasFulfillLotteryParticipant,
        hasReferralCodeInput
    } = useSelector(s => s.userEntryState);

    const { mgmReferral: { uuid: mgmPromoUUID } = {} } = useSelector(s => s.mgmRelation);
    const currentPromoUUID = Boolean(isMgmShare) ? mgmPromoUUID : promoUUID;
    const [retryMgmLotteryValidationApi, setRetryMgmLotteryValidationApi] = useState(0);

    // 根據給獎條件設定以及 user 答題結果，給出可以配獎的獎項，去執行配獎開獎流程
    function getEligibleLotteriesByCondition(gameResultRecords = []) {
        // 找到目前 entry 的遊戲結果
        const currentEntryRecord = gameResultRecords.find(r => r.uuid === currentEntryUUID) ?? {};
        // 確認是否有多選類型的獎
        const isPromoHavePickLottery = Boolean(
            currentLotteryParticipant?.type === lotteryParticipantTypes.PICK_THE_PRIZE ||
                currentLotteryParticipant?.type === lotteryParticipantTypes.POINTS_REDEEM
        );
        // 受否有即時獎
        const isPromoHaveInstantLottery = Boolean(instantLottery?.uuid);
        const isGameChallengeAllCorrect = checkIsGameChallengeAllCorrect({
            type: gameType,
            gameResult: currentEntryRecord?.gameResult
        });
        // 有多選類型的獎 (好禮自由選或兌點自由選)
        if (isPromoHavePickLottery) {
            // 活動同時除了有好禮自由選還有「參加就送」以及 「參加就送」還沒背配獎給此 entry
            if (
                isPromoHaveInstantLottery &&
                !checkIsUserHaveClaimedInstantPrize(currentEntryRecord)
            ) {
                // 且同時開啟「即時獎」需要全對或挑戰成功的狀況
                if (isLotteryInstantRequiredAllCorrect) {
                    // 全對或挑戰成功
                    if (isGameChallengeAllCorrect) {
                        return [instantLottery];
                    }
                    // 沒全對或挑戰失敗
                    return [];
                }
                return [instantLottery];
            }
            return [];
        }
        const eligibleLotteries = [];
        // 若有答題全對(即給獎條件成立)，返回全部 lottery 資料，不管 user 有無開全對設定
        if (isGameChallengeAllCorrect) {
            if (!checkIsUserHaveClaimedParticipantPrize(currentEntryRecord)) {
                eligibleLotteries.push(animatingLottery);
            }
            if (!checkIsUserHaveClaimedInstantPrize(currentEntryRecord)) {
                eligibleLotteries.push(instantLottery);
            }
            return eligibleLotteries;
        }

        // 若有答題沒全對
        // 且同時開啟「動畫獎」與「即時獎」需要全對或挑戰成功的狀況
        if (isLotteryParticipantRequiredAllCorrect && isLotteryInstantRequiredAllCorrect) {
            return [];
        }

        // 且只開啟「動畫獎」需要全對或挑戰成功的狀況
        if (isLotteryParticipantRequiredAllCorrect) {
            if (!checkIsUserHaveClaimedParticipantPrize(currentEntryRecord)) {
                return [instantLottery];
            }
            return [];
        }

        // 且只開啟「即時獎」需要全對或挑戰成功的狀況
        if (isLotteryInstantRequiredAllCorrect) {
            if (!checkIsUserHaveClaimedInstantPrize(currentEntryRecord)) {
                return [animatingLottery];
            }
            return [];
        }

        // 若都沒有啟用全答正確的限制，則回傳兩個獎項
        return [instantLottery, animatingLottery];
    }

    async function processLotteries(lotteries = []) {
        // result 是動畫獎與即時獎配獎後的結果
        const result = [];
        if (lotteries.length === 0) return result;
        const validLottery = lotteries.filter(lottery => Boolean(lottery.uuid));
        for (const [index, lottery] of validLottery.entries()) {
            try {
                const currentLotteryUUID = lottery.uuid;
                // step 1. 拿 entryLottery + 檢查配獎資格
                await checkIsQualifyToGetWinner({
                    promoUUID: currentPromoUUID,
                    entriesUUID,
                    lotteryUUID: currentLotteryUUID
                });
                // step 2. 配獎
                const winnerResp = await getWinner(
                    currentPromoUUID,
                    entriesUUID,
                    currentLotteryUUID
                );
                result.push({
                    lottery,
                    winnerResp
                });
                // 若要連續執行多個配獎，最後一個獎配完後不需要做 delay，預防 API 429 Too may request error
                if (lotteries.length !== index && validLottery.length > 1) {
                    await delay({ timer: 1500 });
                }
            } catch (error) {
                console.error(
                    `Error processing lottery: type=${lottery.type}, uuid=${lottery.uuid}, message=${error}`
                );
            }
        }
        return result;
    }
    // 拿配獎完的 response 資料與 current lottery 做後續開獎、導轉換頁與 error handling
    async function continueFlowAfterGetWinner(result = []) {
        if (result.length === 0) return history.push(RESULT);
        for (const [index, { lottery, winnerResp }] of result.entries()) {
            if (winnerResp.httpStatus === 200) {
                //一次開所有lotteryInstant For 打開 taskFilter
                // https://app.clickup.com/36226806/v/dc/12hhqp-21878/12hhqp-54118
                const prizeUUIDs = winnerResp.payload;
                for (const [innerIndex, prizeUUID] of prizeUUIDs.entries()) {
                    const isLast =
                        index === result.length - 1 && innerIndex === prizeUUIDs.length - 1;
                    await continueFlowSuccessGetWinner(lottery, prizeUUID, isLast);
                }
            } else if (winnerResp.httpStatus === 226) {
                await continueFlowIfNoRemainPrizeAndNoSuspend(lottery);
            } else if (winnerResp.httpStatus === 400) {
                if (
                    winnerResp.payload.message === clientErrTypes.POINTS_IS_NOT_ENOUGH_BY_PRE_CHECK
                ) {
                    winnerResp.errSerialNo = currentEntryId;
                    throw generateApiErrObj(
                        winnerResp,
                        clientErrTypes.POINTS_IS_NOT_ENOUGH_BY_PRE_CHECK
                    );
                } else if (winnerResp.payload.message === clientErrTypes.POINTS_IS_NOT_ENOUGH) {
                    winnerResp.errSerialNo = currentEntryId;
                    throw generateApiErrObj(winnerResp, clientErrTypes.POINTS_IS_NOT_ENOUGH);
                } else {
                    winnerResp.errSerialNo = currentEntryId;
                    throw generateApiErrObj(winnerResp, clientErrTypes.GET_WINNER);
                }
            } else {
                winnerResp.errSerialNo = currentEntryId;
                throw generateApiErrObj(winnerResp, clientErrTypes.GET_WINNER);
            }
        }
    }
    async function checkIsNeedToGetWinner(gameResultRecords) {
        const verifyModuleStatus = getVerifyModuleStatus(gameResultRecords);
        if (
            (isMgmMission || isMgmSelf) &&
            (!verifyModuleStatus.isNeedVerifyModule ||
                (verifyModuleStatus.isNeedVerifyModule && verifyModuleStatus.canGetWinner)) &&
            hasReferralCodeInput
        ) {
            // 進到此流程代表已經填表且有 GameResult，如果是任務模組，要通知 mgm referral 模組
            try {
                await createMgmEntryApi({
                    promoUuid: promoUUID,
                    entryUuid: entriesUUID
                });

                await mgmLotteryValidationApi({
                    promoUuid: promoUUID,
                    entryUuid: entriesUUID
                });
            } catch (err) {
                if (
                    err.message ===
                    clientErrTypes.MGM_LOTTERY_VALIDATION_FAILED.REFERRAL_ENTRY_NOT_FOUND
                ) {
                    setRetryMgmLotteryValidationApi(1);
                } else errHandler({ err });
            }
        }

        // 純獎模組 好禮自由選 與 兑點自由選 的 檢查配獎資格 (checkIsQualifyToGetWinner) 與 配獎 (Get Winner) 寫在各自模組內，而非寫在共用的 useGetParticipantWinner 內
        if (gameType === gameTypes.PICK_THE_PRIZE || gameType === gameTypes.POINTS_REDEEM) {
            // 如果模組是純獎模組且是自由選，且有設定即時獎，在進入動畫頁前，先把即時獎做配獎與開獎，成功後再經由各自模組執行自己動畫獎的配獎與開獎(此配開會寫在各自模組動畫元件內)
            if (instantLottery.uuid) {
                const result = await processLotteries([instantLottery]);
                await delay({ timer: 1500 });
                await continueFlowAfterGetWinner(result);
            }
            return false;
        }
        if (hasFulfillLotteryParticipant && hasFulfillLotteryInstant) {
            if (isNoResultPageGames(gameType)) {
                dispatch(setParticipantWinnerPopup({ isNoGetPrize: true }));
                history.push(JOIN);
            } else {
                history.push(RESULT);
            }
            return false;
        }
        if (verifyModuleStatus.isNeedVerifyModule && !verifyModuleStatus.canGetWinner) {
            if (isNoResultPageGames(gameType)) {
                dispatch(updateGameResultRecordsCount());
                history.push(JOIN);
            } else {
                history.push(RESULT);
            }
            return false;
        }
        if (!verifyModuleStatus.canGetWinner) {
            history.push(RESULT);
            return false;
        }
        return true;
    }
    // 若獎品已經發完的後續流程: 更新 user entry state + 更新剩餘獎品數量 + 跳頁
    async function continueFlowIfNoRemainPrizeAndNoSuspend(lottery) {
        dispatch(successGetWinner(lottery.type, currentLotteryParticipant.type));
        await dispatch(updateParticipantRemainder(0));
        if (isNoResultPageGames(gameType)) {
            dispatch(setParticipantWinnerPopup({ isNoGetPrize: true }));
            history.push(JOIN);
        } else {
            history.push(RESULT);
        }
    }
    function continueFlowSuccessPickWinner() {
        if (isNoResultPageGames(gameType)) {
            dispatch(
                setParticipantWinnerPopup({
                    prizeUUID: PICK_PRIZE_CUSTOM_PRIZE_UUID,
                    entryUUID: entriesUUID
                })
            );
            history.push(JOIN);
        } else {
            if (isGameShouldOpenWinnerAfterGet(gameType)) {
                scrollPromoBodyIntoView();
                history.push(LOTTERY, { animatingLottery, entriesUUID });
            } else {
                // 好禮自由選是在動畫頁，每選一個獎品，就配＋開一個獎，所以不會在 store game result 後馬上打配獎API，而是直接前往結果頁
                history.push(RESULT);
            }
        }
    }
    async function continueFlowSuccessGetWinner(
        currentLottery,
        prizeUUID,
        isGetLastWinnerInMultipleWinners
    ) {
        const { payload: prizeData } = await getPrizeInfoAPI(currentPromoUUID, prizeUUID);
        const { prize: { name: prizeName, type: prizeType } = {} } = prizeData;
        const lotteryCategory = getLotteryConfig(currentLottery.type).category;
        // Support 已上線舊活動是 參加就送 放在 lottery participant
        if (
            lotteryCategory === lotteryCategories.IS_PARTICIPANT_LOTTERY ||
            (currentLottery.type === currentLotteryParticipant.type &&
                currentLottery.type === lotteryInstantTypes.JOIN_AND_GET)
        ) {
            dispatch(successGetWinner(currentLottery.type, currentLotteryParticipant.type));
            dispatch(
                updateFlowOfEntryRecord({
                    entryUUID: entriesUUID,
                    key: entryFlowKeys.LOTTERY_PARTICIPANT
                })
            );
        } else if (lotteryCategory === lotteryCategories.IS_INSTANT_LOTTERY) {
            dispatch(successGetWinner(currentLottery.type));
            dispatch(
                updateFlowOfEntryRecord({
                    entryUUID: entriesUUID,
                    key: entryFlowKeys.LOTTERY_INSTANT
                })
            );
        }
        await dispatch(updateParticipantRemainderFromApi());
        // 參加就送不是純獎模組，也不是有動畫的獎，所以若在純獎模組內被使用的是動畫，才會有點擊畫面後馬上通知現在動畫頁要執行那種 lottery 動畫，跟執行些獎項
        if (isPurePrizeGame && currentLottery.type !== lotteryInstantTypes.JOIN_AND_GET) {
            dispatch(setShouldUpdateUserWinnerList());
            dispatch(setCurrentPrizeUUID(prizeUUID));
            dispatch(setGameAlreadyToPlay());
            const fromRegex = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9]+\/form/gm;
            const isInFormPage = Boolean(location?.pathname?.match(fromRegex));
            // [#86epp4yhj] 若純獎模組不管是有開即時獎 + 動畫獎 or 只開動畫獎，在配獎過程中，若這是最後一次配獎，填完表單，要返回參加頁跑動畫做開獎
            if (isInFormPage && isGetLastWinnerInMultipleWinners) {
                history.push(JOIN);
            }
        } else if (isNoResultPageGames(gameType)) {
            // 參加就送是唯一自動配加開的 lottery
            if (currentLottery.type === lotteryInstantTypes.JOIN_AND_GET) {
                await updateWinnerAnimationFlag(currentPromoUUID, entriesUUID, prizeUUID);
                gtmPushPrize({
                    prizeName,
                    prizeType: getPrizeTypeLabel(prizeType),
                    loginId,
                    promoId
                });
                dispatch(setShouldUpdateUserWinnerList());
            }
            if (lotteryCategory === lotteryCategories.IS_PARTICIPANT_LOTTERY) {
                dispatch(setParticipantWinnerPopup({ prizeUUID, entryUUID: entriesUUID }));
            }
            history.replace(JOIN);
        } else if (isGameShouldOpenWinnerAfterGet(gameType)) {
            // 參加獎不需前往動畫頁，可以直接在配獎過後做開獎且更新 winner 名單
            if (currentLottery.type === lotteryInstantTypes.JOIN_AND_GET) {
                await updateWinnerAnimationFlag(currentPromoUUID, entriesUUID, prizeUUID);
                gtmPushPrize({
                    prizeName,
                    prizeType: getPrizeTypeLabel(prizeType),
                    loginId,
                    promoId
                });
                dispatch(setShouldUpdateUserWinnerList());
                // 登錄給獎＋參加獎 & 新客加入獎勵＋參加獎 => 參加完成遊戲後，不會導頁去結果頁，也不屬於參加及結果頁的模組，他會直接去獎品頁面，補充: 登錄給獎也不是純獎模組，所以領獎時沒動畫
                if (gameType === gameTypes.SIGN_FORM || gameType === gameTypes.SIGN_UP) {
                    history.replace(PRIZE, {
                        prizeUUID,
                        entriesUUID: entriesUUID
                    });
                    return;
                } else history.push(RESULT);
            } else {
                scrollPromoBodyIntoView();
                history.push(LOTTERY, { animatingLottery: currentLottery, entriesUUID, prizeUUID });
            }
        } else {
            // 參加就送要馬上在配獎後要馬上自動開獎
            if (isLotteryShouldOpenWinnerAfterGet(currentLottery.type)) {
                await updateWinnerAnimationFlag(currentPromoUUID, entriesUUID, prizeUUID);
                gtmPushPrize({
                    prizeName,
                    prizeType: getPrizeTypeLabel(prizeType),
                    loginId,
                    promoId
                });
                dispatch(setShouldUpdateUserWinnerList());
            }
            // 若又同時開即時獎+動畫獎，配獎執行順序是：
            // 拿 entryLottery -> 即時獎配獎 -> 即時獎開獎 -> 拿 entryLottery -> 動畫獎配獎 -> 動畫獎開獎
            // 要確保最後一個獎已經配開獎完才能去結果頁，只有純獎的好禮自由選不需要走這個邏輯，因為當參加就送若執行完後，接著要幫好禮自由選不做配獎、開獎時，不能馬上倒轉去結果頁
            const isPurePickThePrizeGame = isPurePrizeGame && gameType === gameTypes.PICK_THE_PRIZE;
            if (isGetLastWinnerInMultipleWinners && !isPurePickThePrizeGame) {
                dispatch(setShouldUpdateUserWinnerList());
                history.push(RESULT);
            }
        }
    }
    async function createParticipantWinner(gameResultRecords) {
        try {
            if (await checkIsNeedToGetWinner(gameResultRecords)) {
                // 如果此模組 (非純獎模組) 有設定動畫獎，且是自由選系列的獎項 (algorithm = 2)
                if (
                    currentLotteryParticipant.type === lotteryParticipantTypes.PICK_THE_PRIZE ||
                    currentLotteryParticipant.type === lotteryParticipantTypes.POINTS_REDEEM
                ) {
                    // 如果此模組除了有開動畫獎(自由選)還有開即時獎(參加就送)，在進入好禮自由選選獎品前，要先提前配獎開獎即時獎(lotteries)
                    const lotteries = getEligibleLotteriesByCondition(gameResultRecords);
                    // 若好禮自由選有即時獎，要先配獎即時獎，成功後再進入好禮自由選選獎品
                    if (lotteries.length > 0) {
                        const result = await processLotteries(lotteries);
                        await continueFlowAfterGetWinner(result);
                    }
                    const { payload } = await getPromoLotteries(currentPromoUUID);
                    const { prizeRemainder: remainder } = payload.filter(
                        ({ lottery: { uuid } }) => uuid === currentLotteryParticipant.uuid
                    )[0];
                    const hasNoRemainder =
                        currentLotteryParticipant.prizeLimited && remainder === 0;

                    if (hasNoRemainder) {
                        if (currentLotteryParticipant.suspendWhenNoPrize) {
                            const err = generateApiErrObj(
                                {},
                                clientErrTypes.PICK_PRIZE_LOTTERY_NO_REMAINDER
                            );
                            errHandler({ err });
                        } else {
                            await continueFlowIfNoRemainPrizeAndNoSuspend(
                                currentLotteryParticipant
                            );
                        }
                    } else {
                        // 好禮自由選與兌點自由選在 entry 被 store game result 後，會馬上進行檢查配獎資格 + 配獎，成功後才會去動畫頁做開獎
                        continueFlowSuccessPickWinner();
                    }
                } else {
                    const lotteries = getEligibleLotteriesByCondition(gameResultRecords);
                    const result = await processLotteries(lotteries);
                    await continueFlowAfterGetWinner(result);
                }
            }
        } catch (err) {
            errHandler({ err });
        }
    }

    useEffect(() => {
        const retry = async () => {
            try {
                await mgmLotteryValidationApi({
                    promoUuid: promoUUID,
                    entryUuid: entriesUUID
                });

                setRetryMgmLotteryValidationApi(0);
            } catch (err) {
                if (
                    err.message ===
                    clientErrTypes.MGM_LOTTERY_VALIDATION_FAILED.REFERRAL_ENTRY_NOT_FOUND
                ) {
                    setRetryMgmLotteryValidationApi(c => (c > 3 ? 0 : c + 1));
                } else errHandler({ err });
            }
        };

        if (retryMgmLotteryValidationApi > 0 && Boolean(entriesUUID) && Boolean(promoUUID)) {
            retry();
        }
    }, [entriesUUID, promoUUID, retryMgmLotteryValidationApi, errHandler]);

    return gameResultRecords => createParticipantWinner(gameResultRecords);
}
